import React from 'react';
import { MdRefresh } from 'react-icons/md';
import { classifyAdCategory, extractAdFormConfig, getAdFormConfig } from '../../lib/leboncoin/LeboncoinExtApi';
import { LeboncoinAttributesForm } from './LeboncoinAttributesForm';
import { gql, useSuspenseFragment, useMutation } from '@apollo/client';
import { LeboncoinAttributesSection_ListingFragment, LeboncoinAttributesUpdateExternalListingMutation, LeboncoinAttributesUpdateExternalListingMutationVariables } from '../../../codegen/graphql';
import { toast } from 'react-toastify';
import { getTranslatedValue } from '../common/TranslatedValue';

export const LEBONCOIN_ATTRIBUTES_SECTION_FRAGMENT = gql`
  fragment LeboncoinAttributesSection_listing on AdExternalListing {
    id
    lang
    externalData
    ad {
      title
    }
  }
`;

export const LEBONCOIN_ATTRIBUTES_UPDATE_EXTERNAL_LISTING = gql`
  mutation LeboncoinAttributesUpdateExternalListing($id: ID!, $input: UpdateAdExternalListingInput!) {
    updateAdExternalListing(id: $id, input: $input) {
      id
      externalData
    }
  }
`;

interface Category {
  id: string;
  topId: string;
  name: string;
}

interface LeboncoinAttributesSectionProps {
  listing: LeboncoinAttributesSection_ListingFragment;
  onExternalDataChange: (newExternalData: Record<string, any>) => void;
  fieldsKeys: string[];
  itemsDefinitions: any;
}

export function LeboncoinAttributesSection({
  listing,
  onExternalDataChange,
  fieldsKeys,
  itemsDefinitions
}: LeboncoinAttributesSectionProps) {
  const { data: fragmentData } = useSuspenseFragment<LeboncoinAttributesSection_ListingFragment>({
    fragment: LEBONCOIN_ATTRIBUTES_SECTION_FRAGMENT,
    fragmentName: 'LeboncoinAttributesSection_listing',
    from: listing,
  });

  const externalData = fragmentData.externalData || {};

  const [categories, setCategories] = React.useState<Category[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const [updateExternalListing] = useMutation<
    LeboncoinAttributesUpdateExternalListingMutation,
    LeboncoinAttributesUpdateExternalListingMutationVariables
  >(LEBONCOIN_ATTRIBUTES_UPDATE_EXTERNAL_LISTING);

  const handleFetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await classifyAdCategory(getTranslatedValue(fragmentData.ad.title, fragmentData.lang));
      setCategories(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCategory = async (category: Category) => {
    try {
      const formConfig = await extractAdFormConfig(await getAdFormConfig(category.id));

      await updateExternalListing({
        variables: {
          id: fragmentData.id,
          input: {
            externalData: {
              ...externalData,
              category,
              categoryHandlesVariations: formConfig.handlesVariations,
            },
          },
        },
      });

      setCategories([]);
    } catch (error: any) {
      toast.error('Error selecting category:' + error.message);
    }
  };

  const handleChangeAttributes = (data: any) => {
    onExternalDataChange({
      ...externalData,
      mainAttributes: {
        ...externalData.mainAttributes,
        ...data
      }
    });
  };


  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Leboncoin Attributes</h2>

      {/* Category Section */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Category</h3>
        <div className="mb-2">
          {externalData.category ? (
            <div className="text-gray-900">
              {externalData.category.name}
            </div>
          ) : (
            <div className="text-gray-500 italic">
              (no category set)
            </div>
          )}
        </div>
        <button
          onClick={handleFetchCategories}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <MdRefresh className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={20} />
          {isLoading ? 'Fetching...' : 'Fetch categories'}
        </button>

        {/* Categories List */}
        {categories.length > 0 && (
          <div className="mt-4 border rounded-md divide-y">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => handleSelectCategory(category)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between group"
              >
                <span>{category.name}</span>
                <span className="text-blue-600 opacity-0 group-hover:opacity-100">
                  Select
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {fieldsKeys && itemsDefinitions && (
        <LeboncoinAttributesForm
          fieldsToDisplay={fieldsKeys}
          fieldsDefinition={itemsDefinitions}
          data={externalData.mainAttributes || {}}
          onChange={handleChangeAttributes}
        />
      )}
    </div>
  );
}
